// Ported from LC2's VacancyGlance.js (commons/components/HomeTabs/Today/Portfolio/Glance/
// VacancyGlance.js) — same VacancyGlanceColumns row rendering (commons/constants/
// tableColumns.js: property/unit/floor plan/rent/market rent/rent delta/enter market/
// available on/days on market/market DOM/DOM delta/last updated, with the days-on-market
// columns colored red/green at the 45-day threshold), same per-row expand -> `useGetVacancyCompsQuery`
// (accounts/{account}/property/{property}/vacancy/{id}/comps/, id parsed out of the row's
// `PK` the same way LC2 does) fetch of comparable units rendered with vacancyCompColumns, and
// same "no data" empty state (see ./GlanceEmptyState.js). No chart in this component in LC2
// (it's a DataGrid + a lazy per-row "detail panel" — verified before deciding not to add a
// charting dependency, per the task's charting rule).
// SIMPLIFIED: LC2 rendered this with MUI X DataGrid Pro's built-in "detail panel" row
// expansion (a licensed package this app doesn't depend on — see StyledTable/CustomTables.js).
// Rebuilt as a plain MUI Table with a manual expanded-row Set, same approach as
// ../Performance/UnitsWithConcessions.js's ComparisonTable. Export/search/pagination chrome
// for both the main table and the comps panel is dropped (LC2's ExportToolBarWithSearch /
// server-side DataGrid pagination) in favor of a plain scrollable table — the comps fetch
// itself still requests page 0 / a larger page size in one shot rather than paginating,
// since there's no pager UI here to drive further pages.
import React, {useState} from 'react';
import PropTypes from 'prop-types';

import {Box, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';

import {tokens} from '../../../themes/ThemeCustomization';
import {useGetVacancyCompsQuery} from '../../../features/coreApi/metricsSlice';
import LoadingBox from '../../../commons/components/LoadingBox';
import GlanceCell from './GlanceCell';
import GlanceEmptyState from './GlanceEmptyState';

const MAIN_COLUMNS = [
    {field: 'property_name', headerName: 'Property'},
    {field: 'unit_name', headerName: 'Unit'},
    {field: 'floorplan_name', headerName: 'Floor Plan'},
    {field: 'price', headerName: 'Rent', format: 'currency'},
    {field: 'avg_comp_rent', headerName: 'Market Rent', format: 'currency'},
    {field: 'rent_delta', headerName: 'Rent Delta', format: 'percentage', color: 'red/green', colorValue: 0},
    {field: 'enter_market', headerName: 'Enter Market', format: 'date'},
    {field: 'availability', headerName: 'Available On', format: 'date'},
    {field: 'days_on_market', headerName: 'Days On Market', format: 'numeric', color: 'red/green', colorValue: 45},
    {field: 'avg_comp_days_on_market', headerName: 'Market DOM', format: 'numeric', color: 'red/green', colorValue: 45},
    {field: 'dom_delta', headerName: 'DOM Delta', format: 'percentage'},
    {field: 'last_update', headerName: 'Last Updated', format: 'date'}
];

const COMP_COLUMNS = [
    {field: 'building_name', headerName: 'Property Name'},
    {field: 'bed', headerName: 'Bed', format: 'numeric'},
    {field: 'bath', headerName: 'Bath', format: 'numeric'},
    {field: 'sqft', headerName: 'Sqft', format: 'numeric'},
    {field: 'price', headerName: 'Price', format: 'currency'},
    {field: 'floorplan_name', headerName: 'Floor Plan'},
    {field: 'unit_name', headerName: 'Unit Name'},
    {field: 'enter_market', headerName: 'Enter Market', format: 'date'},
    {field: 'exit_market', headerName: 'Exit Market', format: 'date'},
    {field: 'days_on_market', headerName: 'Days On Market', format: 'numeric', color: 'red/green', colorValue: 45},
    {field: 'availability', headerName: 'Availability On', format: 'date'},
    {field: 'similarity', headerName: 'Similarity', format: 'percentage'}
];

const ComparisonPanel = ({row}) => {
    const {
        data: compsData,
        isLoading,
        isFetching,
        isError
    } = useGetVacancyCompsQuery(
        {
            account_sfid: row?.account_sfid,
            property_sfid: row?.property_sfid,
            id: row?.PK?.split('#')[2],
            params: {page: 0, page_size: 20}
        },
        {skip: !row?.PK || !row?.account_sfid || !row?.property_sfid, keepUnusedDataFor: 600}
    );

    const comps = Array.isArray(compsData?.data) ? compsData.data : [];

    return (
        <Box sx={{width: '100%', p: 2, backgroundColor: tokens.color.neutral50}}>
            <Typography variant="subtitle2" sx={{pb: 1}}>
                Comparing to {compsData?.pagination?.total ?? comps.length} similar units
            </Typography>
            {isLoading || isFetching ? (
                <LoadingBox height={120} />
            ) : (
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {COMP_COLUMNS.map((col) => (
                                    <TableCell key={col.field} sx={{fontWeight: 500, whiteSpace: 'nowrap'}}>
                                        {col.headerName}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {comps.map((comp, idx) => (
                                <TableRow key={comp.id || `${comp.building_id}-${comp.unit_name}-${idx}`}>
                                    {COMP_COLUMNS.map((col) => (
                                        <TableCell key={col.field}>
                                            <GlanceCell value={comp[col.field]} format={col.format} color={col.color} colorValue={col.colorValue} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {comps.length === 0 && !isError && (
                                <TableRow>
                                    <TableCell colSpan={COMP_COLUMNS.length} sx={{textAlign: 'center', color: 'text.secondary'}}>
                                        No comparable units found
                                    </TableCell>
                                </TableRow>
                            )}
                            {isError && (
                                <TableRow>
                                    <TableCell colSpan={COMP_COLUMNS.length} sx={{textAlign: 'center', color: 'error.main'}}>
                                        Error loading comparison data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
};

ComparisonPanel.propTypes = {row: PropTypes.object};

const VacancyGlance = ({data}) => {
    const rows = data || [];
    const [expandedRows, setExpandedRows] = useState(new Set());

    const toggleExpanded = (id) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    if (rows.length === 0) {
        return <GlanceEmptyState msg="No vacant units found on public listing sites" type="Glance Metric" />;
    }

    return (
        <Stack direction="column">
            <Stack sx={{p: 2}}>
                <Typography variant="subtitle2" fontWeight="fontWeightRegular" color="text.secondary">
                    All your units that we see available for rent on public listings sites. (&#39;DOM&#39; = Days on Market)
                </Typography>
            </Stack>
            <Box sx={{overflowX: 'auto'}}>
                <Table>
                    <TableHead>
                        <TableRow sx={{backgroundColor: tokens.color.neutral50}}>
                            <TableCell sx={{width: 40}} />
                            {MAIN_COLUMNS.map((col) => (
                                <TableCell key={col.field} sx={{fontWeight: 500, whiteSpace: 'nowrap'}}>
                                    {col.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const rowId = row.id;
                            const isExpanded = expandedRows.has(rowId);

                            return (
                                <React.Fragment key={rowId}>
                                    <TableRow>
                                        <TableCell sx={{width: 40}}>
                                            <IconButton size="small" onClick={() => toggleExpanded(rowId)}>
                                                {isExpanded ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        {MAIN_COLUMNS.map((col) => (
                                            <TableCell key={col.field}>
                                                <GlanceCell value={row[col.field]} format={col.format} color={col.color} colorValue={col.colorValue} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={MAIN_COLUMNS.length + 1} sx={{p: 0, border: isExpanded ? undefined : 'none'}}>
                                            <Collapse in={isExpanded}>{isExpanded && <ComparisonPanel row={row} />}</Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        </Stack>
    );
};

VacancyGlance.propTypes = {
    data: PropTypes.array
};

export default VacancyGlance;
